import {useAppDispatch, useAppSelector} from "../store";
import {selectAuthInfo, selectCodebooks} from "../store/selectors";
import * as React from "react";
import {CSSProperties, useCallback, useEffect, useMemo, useState} from "react";
import {Chip} from "@mui/material";
import {useAppTranslation} from "../services/i18n";
import {JsonPartyEventDetails, JsonPartyEventDetailsInviteStatusEnum, JsonPartyMassActionRequest, JsonPartyMassActionRequestActionEnum} from "../generated-api";
import {massParty} from "../store/parties";
import {getApiResult} from "../helpers/api";
import {FIXED_INVITE_TAG_ID} from "../store/codebooks";
import {shorten} from "../helpers/format";
import {AuthState} from "../store/auth";

type ColorItem = {
    color: string;
    count: number;
    title: string;
};

const generatePieBackground = (items: ColorItem[]): [CSSProperties, string?] => {

    const totalCount = items.reduce((sum, item) => sum + item.count, 0);

    let currentAngle = 0;
    const gradientParts: string[] = [];

    const titles: string[] = [];
    items.forEach((item) => {
        if (item.count <= 0) return;

        const angle = (item.count / totalCount) * 360;
        const startAngle = currentAngle;
        const endAngle = currentAngle + angle;
        gradientParts.push(`${item.color} ${startAngle}deg ${endAngle}deg`);
        currentAngle = endAngle;

        titles.push(item.title + ": " + item.count + "x");
    });

    return [{
        background: `conic-gradient(${gradientParts.join(', ')})`,
        color: '#fff'
    },
        titles.join("\n")
    ];
};

type PartyTagProps = {
    tags?: number[] | undefined,
    quickEditTagId?: number,
    partyId?: number,
    short?: boolean,
    eventDetails?: JsonPartyEventDetails[],
    eventIds?: number[]
}

const PartyTags = (props: PartyTagProps) => {
    const {tags, quickEditTagId, partyId, short, eventDetails, eventIds} = props;

    const t = useAppTranslation();
    const dispatch = useAppDispatch();
    const codebooks = useAppSelector(selectCodebooks);
    const {configuration} = useAppSelector<AuthState>(selectAuthInfo);
    const [myTags, setMyTags] = useState<number[] | undefined>();
    const tagOptions = codebooks['tag'];

    const handleQuickTagSave = useCallback(async (partyId: number, tagId: number, isAdd: boolean) => {
        const ma: JsonPartyMassActionRequest = {
            action: JsonPartyMassActionRequestActionEnum.UpdateTags,
            items: [{
                partyId,
                tags: isAdd ? [tagId] : undefined,
                removeTags: !isAdd ? [tagId] : undefined
            }]
        }
        const res = await dispatch(massParty({request: ma}));
        const items = getApiResult(res);
        return items?.[0]?.tags;

    }, [dispatch]);

    const items: JSX.Element[] | null = useMemo(() => {
        if (!tagOptions || (!myTags?.length && !quickEditTagId)) {
            return null;
        }
        const hasQuickTag = !!quickEditTagId && myTags?.find((t) => t === quickEditTagId);

        const items: JSX.Element[] = [];
        if (quickEditTagId && !hasQuickTag) {
            const o = tagOptions.find((o) => o?.value === "" + quickEditTagId);
            if (o) {
                items.push(<Chip key={'quick'} size={'small'} className={'party-tags-add'}
                    label={'+'} title={t('Kliknutím přidat štítek {{title}}', {title: o.label})}
                    // style={o.params?.color ? {backgroundColor: o.params.color as any as string} : undefined}
                    onClick={async () => {
                        if (partyId) {
                            setMyTags(await handleQuickTagSave(partyId, quickEditTagId, true));
                        }
                    }}
                />)
            }
        }
        if (myTags && myTags.length && myTags.length > 0) {
            tagOptions
                ?.filter((o, i) => o?.value && myTags?.indexOf(parseInt(o.value, 10)) >= 0)
                .forEach((o, i) => {
                    const tagId = +o.value!;
                    let style: CSSProperties | undefined = o.params?.color ? {backgroundColor: o.params.color as any as string} : undefined;
                    let title = t('Kliknutím odebrat štítek {{title}}', {title: o.label});
                    if (eventDetails && tagId === FIXED_INVITE_TAG_ID) {
                        let acceptedNone = 0, acceptedAny = 0, expired = 0, rejected = 0;
                        eventDetails.forEach((ed) => {
                            const eventId = ed.eventId;
                            if (!eventId || !ed.inviteStatus || (eventIds?.length && eventIds.indexOf(eventId) < 0)) {
                                return;
                            }
                            if (configuration?.defaultEvent?.eventId && configuration?.defaultEvent?.eventId === eventId) {
                                return;
                            }
                            if (ed.inviteStatus === JsonPartyEventDetailsInviteStatusEnum.Confirmed) {
                                if (ed.realEventDays && ed.realEventDays.indexOf("1") >= 0) {
                                    acceptedAny++;
                                } else if (eventId >= 20) { // realEventDays since
                                    acceptedNone++;
                                }

                            } else if (ed.inviteStatus === JsonPartyEventDetailsInviteStatusEnum.Expired) {
                                expired++;

                            } else if (ed.inviteStatus === JsonPartyEventDetailsInviteStatusEnum.Rejected) {
                                rejected++;

                            }
                        });
                        if (acceptedAny + acceptedNone + expired + rejected) {
                            const b = generatePieBackground([
                                {color: 'var(--green)', count: acceptedAny, title: t('Přijala, přišla')},
                                {color: 'orangered', count: acceptedNone, title: t('Přijala, nepřišla')},
                                {color: 'maroon', count: rejected, title: t('Odmítla účast')},
                                {color: 'gray', count: expired, title: t('Nereagovala na pozvánku')},
                            ]);
                            if (b[0]) {
                                style = b[0];
                                title = b[1] + "\n\n" + title;
                            }
                        }
                    }

                    if (quickEditTagId && tagId === quickEditTagId) {
                        items.push(<Chip key={i} size={'small'} className={'party-tags-remove'}
                            label={o.label && o.label[0]}
                            title={title}
                            onClick={async () => {
                                if (partyId) {
                                    setMyTags(await handleQuickTagSave(partyId, quickEditTagId, false));
                                }
                            }}
                            style={style}/>)
                        return;
                    }
                    items.push(<Chip key={i} size={'small'}
                        label={tagId === FIXED_INVITE_TAG_ID ? o.label?.[0] : (short ? shorten(o.label) : o.label)}
                        title={short ? o.label : undefined}
                        style={style}/>)
                });
        }

        return items;

    }, [myTags, quickEditTagId, handleQuickTagSave, partyId, tagOptions, short, eventDetails, eventIds, configuration?.defaultEvent?.eventId, t]);

    useEffect(() => {
        setMyTags(tags)
    }, [tags]);

    return items ? <div className={'party-tags'}>{items}</div> : null;
}

export default PartyTags;
